import { Fragment, useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Select, Skeleton } from '../../design-system';
import { hhmm, clockTime } from './AttendanceClock';
import DayTimeline from './DayTimeline';
import '../../org-admin/styles/Attendance.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

// One person's month: every day, worked hours, overtime, and the totals.
//
// `userId` omitted = my own (`/me`); supplied = a manager reading someone
// else's (`/user/:id`). Both hit the same server-side builder, so the employee
// and their manager can never see different numbers for the same day.

const STATUS = {
  present: { label: 'Present', cls: 'att-s-present' },
  half_day: { label: 'Half day', cls: 'att-s-half' },
  absent: { label: 'Absent', cls: 'att-s-absent' },
  leave: { label: 'Leave', cls: 'att-s-leave' },
  holiday: { label: 'Holiday', cls: 'att-s-holiday' },
  week_off: { label: 'Week off', cls: 'att-s-off' },
  upcoming: { label: '—', cls: 'att-s-upcoming' },
};

const monthOptions = (count = 12) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i += 1) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value: ym, label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

const dayLabel = (dateKey) => {
  const d = new Date(`${dateKey}T00:00:00Z`);
  return {
    num: d.getUTCDate(),
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()],
  };
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function AttendanceMonth({ userId, title = 'My attendance', refreshKey }) {
  const revision = useLiveRefresh();
  const months = monthOptions();
  const [ym, setYm] = useState(months[0].value);
  // Which day's timeline is open. One at a time — this sits inside a table
  // row, and two expanded panels make the month impossible to scan.
  const [expanded, setExpanded] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const url = userId ? `/api/attendance/user/${userId}` : '/api/attendance/me';
    apiClient
      .get(url, { params: { month: ym } })
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Could not load attendance'))
      .finally(() => setLoading(false));
  }, [userId, ym]);

  // `refreshKey` is the parent's signal; `revision` is the app's — a day
  // marked from the layout's dialog reaches this table through the second.
  useEffect(load, [load, refreshKey, revision]);

  const exportCsv = () => {
    if (!data) return;
    const header = ['Date', 'Day', 'Status', 'In', 'Out', 'Worked', 'Break', 'Overtime', 'Note'];
    const body = data.days.map((d) => [
      d.dateKey, dayLabel(d.dateKey).dow, STATUS[d.status]?.label || d.status,
      clockTime(d.firstInAt), clockTime(d.lastOutAt),
      hhmm(d.workedMinutes), hhmm(d.breakMinutes), hhmm(d.overtimeMinutes),
      d.note || d.dayLabel || '',
    ]);
    const csv = [header, ...body].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${ym}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.totals;

  return (
    <Card>
      <div className="att-head">
        <div>
          <h3 className="att-head-title">{title}</h3>
          <p className="att-head-sub">
            {data ? `Standard day ${hhmm(data.standardMinutes)} · ${data.timezone}` : ' '}
          </p>
        </div>
        <div className="att-head-actions">
          <Select value={ym} onChange={(e) => setYm(e.target.value)} aria-label="Month">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!data}>Export</Button>
        </div>
      </div>

      {loading ? (
        <Skeleton.Text lines={8} />
      ) : error ? (
        <p className="att-error">{error}</p>
      ) : (
        <>
          <div className="att-tiles">
            <div className="att-tile">
              <span className="att-tile-label">Worked</span>
              <span className="att-tile-value">{t.workedLabel}</span>
              <span className="att-tile-hint">of {t.expectedLabel} expected</span>
            </div>
            <div className="att-tile att-tile-over">
              <span className="att-tile-label">Overtime</span>
              <span className="att-tile-value">{t.overtimeLabel}</span>
              <span className="att-tile-hint">beyond the standard day</span>
            </div>
            <div className="att-tile">
              <span className="att-tile-label">Balance</span>
              <span className={`att-tile-value ${t.balanceMinutes >= 0 ? 'att-pos' : 'att-neg'}`}>
                {t.balanceMinutes >= 0 ? '+' : '−'}{hhmm(Math.abs(t.balanceMinutes))}
              </span>
              <span className="att-tile-hint">worked vs expected</span>
            </div>
            <div className="att-tile">
              <span className="att-tile-label">Days</span>
              <span className="att-tile-value">{t.present + t.halfDays}</span>
              <span className="att-tile-hint">
                {t.halfDays > 0 && `${t.halfDays} half · `}
                {t.absent} absent{t.leave > 0 ? ` · ${t.leave} leave` : ''}
              </span>
            </div>
          </div>

          <div className="att-scroll">
            <table className="att-table">
              <thead>
                <tr>
                  <th>Date</th><th>Status</th><th>In</th><th>Out</th>
                  <th className="att-num">Worked</th>
                  <th className="att-num">Break</th>
                  <th className="att-num">Overtime</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => {
                  const s = STATUS[d.status] || STATUS.upcoming;
                  const { num, dow } = dayLabel(d.dateKey);
                  // Only a day with something recorded has a shape worth
                  // opening; a weekly off would expand to an empty panel.
                  const hasDetail = (d.sessions || []).length > 0;
                  const isOpen = expanded === d.dateKey;
                  return (
                    <Fragment key={d.dateKey}>
                    <tr
                      className={`${d.status === 'upcoming' ? 'att-row-future' : ''}${hasDetail ? ' att-row-clickable' : ''}`}
                      onClick={hasDetail ? () => setExpanded(isOpen ? null : d.dateKey) : undefined}
                    >
                      <td className="att-nowrap">
                        <span className="att-daynum">{num}</span>
                        <span className="att-dow">{dow}</span>
                      </td>
                      <td>
                        <span className={`att-pill ${s.cls}`}>{s.label}</span>
                        {d.isOpen && <span className="att-pill att-s-open">In now</span>}
                      </td>
                      <td className="att-nowrap">{clockTime(d.firstInAt)}</td>
                      <td className="att-nowrap">{clockTime(d.lastOutAt)}</td>
                      <td className="att-num">{d.workedMinutes ? hhmm(d.workedMinutes) : '—'}</td>
                      <td className="att-num att-muted">{d.breakMinutes ? hhmm(d.breakMinutes) : '—'}</td>
                      <td className="att-num">
                        {d.overtimeMinutes ? <b className="att-pos">+{hhmm(d.overtimeMinutes)}</b> : '—'}
                      </td>
                      <td className="att-note">
                        {d.note || d.dayLabel || ''}
                        {d.editedByName && (
                          <span className="att-edited" title={d.editReason}>
                            edited by {d.editedByName}
                          </span>
                        )}
                        {hasDetail && (
                          <span className="att-sessions">
                            {d.sessions.length} {d.sessions.length === 1 ? 'session' : 'sessions'}
                            {' '}{isOpen ? '▴' : '▾'}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="att-row-detail">
                        <td colSpan={8}>
                          <DayTimeline day={d} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

export default AttendanceMonth;
export { STATUS, monthOptions };
